// Admin API(Phase 4.5):用户管理 + 仓库管理 + 备份管理,仅 admin 可调用。
// 所有请求走 apiClient,自动携带 Bearer token;后端 RequireAdmin 校验 is_admin。
import { apiClient, ApiError } from './client';
import { useAuthStore } from '@/store/auth';
import type { PublicUser } from './types';

export interface AdminUserList {
  users: PublicUser[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateUserReq {
  username: string;
  email: string;
  password: string;
  display_name?: string | null;
  is_admin?: boolean;
}

export interface PatchUserReq {
  is_admin?: boolean;
  status?: 'active' | 'suspended' | 'pending';
  display_name?: string | null;
}

export interface ResetPasswordReq {
  new_password: string;
}

// ── 仓库管理 ──

export interface AdminRepo {
  id: string;
  owner: string;
  name: string;
  description: string | null;
  visibility: 'public' | 'private';
  default_branch: string;
  size_bytes: number;
  created_at: string;
  updated_at: string;
}

export interface AdminRepoList {
  repos: AdminRepo[];
  total: number;
  limit: number;
  offset: number;
}

export const adminApi = {
  /** 列出所有用户(分页) */
  async listUsers(limit = 50, offset = 0): Promise<AdminUserList> {
    const { data } = await apiClient.get<AdminUserList>(
      `/v1/admin/users?limit=${limit}&offset=${offset}`,
    );
    return data;
  },

  /** 管理员新建用户(可指定是否管理员) */
  async createUser(payload: CreateUserReq): Promise<PublicUser> {
    const { data } = await apiClient.post<{ user: PublicUser }>(
      '/v1/admin/users',
      payload,
    );
    return data.user;
  },

  /** 修改用户:is_admin / status / display_name(按需传) */
  async patchUser(username: string, payload: PatchUserReq): Promise<PublicUser> {
    const { data } = await apiClient.patch<{ user: PublicUser }>(
      `/v1/admin/users/${encodeURIComponent(username)}`,
      payload,
    );
    return data.user;
  },

  /** 重置用户密码(管理员代操作) */
  async resetPassword(username: string, new_password: string): Promise<void> {
    await apiClient.post(
      `/v1/admin/users/${encodeURIComponent(username)}/reset-password`,
      { new_password } as ResetPasswordReq,
    );
  },

  /** 删除用户(不能删自己 / 最后一个 admin,由后端保护) */
  async deleteUser(username: string): Promise<void> {
    await apiClient.delete(
      `/v1/admin/users/${encodeURIComponent(username)}`,
    );
  },

  // ── 仓库管理(admin 可操作所有仓库) ──

  /** 列出所有仓库(分页) */
  async listRepos(limit = 50, offset = 0): Promise<AdminRepoList> {
    const { data } = await apiClient.get<AdminRepoList>(
      `/v1/admin/repos?limit=${limit}&offset=${offset}`,
    );
    return data;
  },

  /** 强制删除任意仓库(admin 权限,不需 owner) */
  async forceDeleteRepo(owner: string, name: string): Promise<void> {
    await apiClient.delete(
      `/v1/admin/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`,
    );
  },

  // ── 备份管理(Phase 5) ──

  /** 列出所有备份文件 */
  async listBackups(): Promise<BackupListResponse> {
    const { data } = await apiClient.get<BackupListResponse>('/v1/admin/backups');
    return data;
  },

  /** 手动触发一次完整备份(返回 manifest) */
  async triggerBackup(): Promise<BackupManifest> {
    const { data } = await apiClient.post<BackupManifest>('/v1/admin/backups');
    return data;
  },

  /** 删除单个备份文件 */
  async deleteBackup(name: string): Promise<void> {
    await apiClient.delete(`/v1/admin/backups/${encodeURIComponent(name)}`);
  },

  /** 下载单个备份文件(流式,触发浏览器下载) */
  async downloadBackup(name: string): Promise<void> {
    const token = useAuthStore.getState().accessToken;
    const resp = await fetch(`/api/v1/admin/backups/${encodeURIComponent(name)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      throw new ApiError(resp.status, 'internal', `下载失败 (HTTP ${resp.status})`);
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /** 读取系统设置(admin) */
  getSettings,
  /** 更新系统设置(admin) */
  updateSettings,
};

// ── 备份相关类型 ──

// ── 系统设置(Phase 6) ──

export interface BackupSettings {
  auto_enabled: boolean;
  auto_schedule: string;
  keep_days: number;
}

export interface GeneralSettings {
  site_title: string;
  allow_registration: boolean;
  default_visibility: 'public' | 'private';
  session_ttl_mins: number;
  max_upload_mb: number;
  /**
   * 鉴权模式:
   *   "local"      本地账号 + OAuth 均可登录(默认)
   *   "oauth_only" 禁用本地登录/注册,只允许 OAuth;后端 403 护栏独立生效
   */
  auth_mode: 'local' | 'oauth_only';
  /**
   * 对外仓库服务器域名/IP(可选)。留空时前端用浏览器访问到的 hostname 兜底。
   * 用于拼 clone URL —— gitman 作为独立产品,clone 应直连 gitman 自身端口,不经 API 网关。
   * 仅为裸域名/IP,不含协议、端口与路径。
   * 必填字段(null 表示留空回落 config):保存时总是提交完整三项,避免全量覆盖清空未填项。
   */
  external_host: string | null;
  /** 对外 HTTP 端口(可选,NAT/Docker -p 映射后的对外端口)。留空回落服务端 config。 */
  external_http_port: number | null;
  /** 对外 HTTPS 端口(可选,仅 HTTPS 启用时有效)。留空回落服务端 config。 */
  external_https_port: number | null;
}

// ── OAuth / SSO 设置(Phase 6) ──
// 注意:client_secret 不在此结构中(由后端从 Config 注入,绝不下发前端)。

export interface OAuthProviderConfig {
  /** 唯一 key,用于路由 /auth/oauth/{name} 与本地账号绑定 */
  name: string;
  /** 是否启用(登录页是否出现按钮) */
  enabled: boolean;
  /** 登录按钮文案 */
  display_name: string;
  /** IdP 基址(不含末尾斜杠) */
  base_url: string;
  /** OAuth2 client_id */
  client_id: string;
  /** 请求的 scope(空格分隔;需含 openid profile email 才能拿到完整 profile) */
  scopes: string;
  /** 流程类型:"proxy"(代理登录)或 "authorization_code"(标准授权码 + PKCE) */
  flow: string;
  /** 首次登录、本地无对应账号时是否自动创建本地账号 */
  auto_create: boolean;
  /** 可选:覆盖回调 / redirect_uri */
  redirect_uri?: string | null;
  /** OIDC Discovery 文档地址(可选,标准授权码流程优先)。填了自动发现端点,实现仅配 issuer 即可 */
  discovery_url?: string | null;
  /** 显式标准流程端点(可选,优先级低于 discovery_url,高于 base_url 推导) */
  authorization_endpoint?: string | null;
  token_endpoint?: string | null;
  userinfo_endpoint?: string | null;
  /** 登录按钮 / 列表展示用的图标 URL(可选;http(s) 或 data: URI),仅用于 UI 展示 */
  icon?: string | null;
  /**
   * 浏览器跳转用的授权端点基址(可选,仅标准授权码流程)。
   * 当浏览器与 gitman 后端不在同一网络(如容器部署)时,两者能解析的 IdP 主机不同:
   * - `base_url` 用后端可达地址(如 http://ming-gateway:8080)去换 token / 拉 userinfo;
   * - 本字段填浏览器可达地址(如 http://localhost:8080),授权跳转才打得到 IdP。
   * 留空则授权跳转与后端共用 base_url(同域部署)。
   */
  authorize_base_url?: string | null;
}

export interface OAuthSettings {
  /** 总开关:关闭后即使有 provider 也不暴露登录入口 */
  enabled: boolean;
  providers: OAuthProviderConfig[];
}

export interface RuntimeSettings {
  backup: BackupSettings;
  general: GeneralSettings;
  oauth: OAuthSettings;
}

/** 读取完整系统设置(admin) */
async function getSettings(): Promise<RuntimeSettings> {
  const { data } = await apiClient.get<RuntimeSettings>('/v1/admin/settings');
  return data;
}

/** 更新完整系统设置(admin,整份替换) */
async function updateSettings(payload: RuntimeSettings): Promise<RuntimeSettings> {
  const { data } = await apiClient.put<RuntimeSettings>('/v1/admin/settings', payload);
  return data;
}

export type BackupKind = 'git_source' | 'git_history' | 'database' | 'manifest';

export interface BackupFileInfo {
  name: string;
  kind: BackupKind;
  size: number;
  sha256: string;
  created_at: string;
}

export interface BackupListResponse {
  files: BackupFileInfo[];
}

export interface BackupManifest {
  ts: string;
  created_at: string;
  files: BackupFileInfo[];
}

/** 备份种类的中文标签(用于 UI 展示),按展示顺序排列 */
export const BACKUP_KIND_META: Record<BackupKind, { label: string; color: string }> = {
  git_source: { label: 'Git 源快照', color: 'blue' },
  git_history: { label: 'Git 全量历史', color: 'geekblue' },
  database: { label: 'SQLite 数据库', color: 'green' },
  manifest: { label: '备份清单', color: 'default' },
};
